import { base62ToHex, hexToBase62, } from '@streamr/tv-engine';
import { decodeIndex, importIndexKey, openKeysFile, openWithKey, RECORDING_INDEX_OBJECT, RECORDING_KEYS_OBJECT, RECORDING_THUMBNAIL_OBJECT, RecordingIndex, recordingObjectUrl, utf8, } from '@streamr/tv-engine/~/recording';
import { executeSoon } from '@streamr/tv-utils';
import { hexToBinary } from '@streamr/utils';
import { showToast } from '../../components/popups/ToastPopup';
import { playerDocumentUrl, RECORDING_SW_SCOPE, RECORDING_SW_URL, } from '../../sw/recordingPlaybackProtocol';
import { isWebKitBrowser } from '../../utils/browser';
import { copyToClipboard } from '../../utils/copyToClipboard';
import { formRecordingUrls } from '../../utils/routes';
import { getScopedServiceWorkerRegistration, registerScopedServiceWorker, } from '../../utils/serviceWorker';
import { formBroadcastUrl } from '../../utils/url';
import { RecordingPlaybackServiceOutput } from './RecordingPlaybackServiceOutput';
import { findBroadcasterPrivateKey, verifyBroadcasterSignature, } from './recordingKeys';
const PROVISION_TIMEOUT_MS = 10_000;
const INDEX_KEY_HEX_LENGTH = 64;
/** how often the page looks whether the browser still has the worker */
const WORKER_WATCH_INTERVAL_MS = 2000;
/** a player is built anew this often at most, within the window */
const RECOVERY_LIMIT = 5;
const RECOVERY_WINDOW_MS = 60_000;
/** query parameter of a recordings link that names a recording */
export const RECORDING_LINK_PARAM = 'recording';
/** fragment parameter that carries the index key of a private recording */
export const RECORDING_KEY_LINK_PARAM = 'rk';
/** The service worker went away while the page was talking to it. */
class WorkerLostError extends Error {
    constructor() {
        super('The recording player stopped');
        this.name = 'WorkerLostError';
    }
}
function thumbnailAdditionalData(recordingId) {
    return utf8(`streamrtv-recording-thumbnail-v1:${recordingId}`);
}
function randomToken() {
    return Array.from(crypto.getRandomValues(new Uint8Array(12)), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
/**
 * Playing recordings. A recording is stored as encrypted packets plus an
 * index; this service gets the keys and the index, hands them to the
 * recording service worker, and the worker serves the recording to a
 * <video> element as if it were an MP4 file (see src/sw/recording-sw.ts).
 * Thumbnails and share links are here as well: both need the index key.
 *
 * Safari (every WebKit browser) deletes the stored data of a site nobody
 * has interacted with, service worker registrations included, some
 * seconds after it starts and from time to time after that. A recording
 * opened from a link is such a site until the first tap. There, and only
 * there:
 *  - playback begins with a press on a play button of the player document
 *    (`requirePress`), which is the interaction that makes Safari keep the
 *    worker from then on;
 *  - while a player is open the page watches for the worker, and when it
 *    is gone registers it again and builds the player anew, at the
 *    position the old one had.
 * Other browsers get the player as it always was.
 */
export class RecordingPlaybackService {
    static instance;
    output = new RecordingPlaybackServiceOutput();
    indexKeys = new Map();
    thumbnailsRequested = new Set();
    open;
    static getInstance() {
        if (!RecordingPlaybackService.instance) {
            RecordingPlaybackService.instance = new RecordingPlaybackService();
        }
        return RecordingPlaybackService.instance;
    }
    static getNotificationInterface() {
        return RecordingPlaybackService.getInstance();
    }
    static getOutput() {
        return RecordingPlaybackServiceOutput.getReactiveOutput();
    }
    constructor() {
        window.addEventListener('message', this.onWindowMessage);
    }
    async init() {
        // nothing to prepare: the service worker is registered on first use
    }
    /**
     * Opens a recording for playing. `broadcast` carries the stream key;
     * `indexKeyFromLink` is given when a private recording is opened
     * through a link that was shared.
     */
    notifyOpenRecording(recording, broadcast, indexKeyFromLink) {
        this.notifyClosePlayback();
        const open = {
            recording,
            token: '',
            parts: [],
            phase: 'starting',
            recoveries: [],
        };
        this.open = open;
        this.output.setPlayback({
            status: 'opening',
            recordingId: recording.recordingId,
        });
        executeSoon(() => this.openRecording(open, broadcast, indexKeyFromLink), (error) => {
            this.fail(open, error);
        });
    }
    notifyClosePlayback() {
        const open = this.open;
        this.open = undefined;
        this.output.setPlayback(undefined);
        if (open) {
            open.stopWatching?.();
            this.releaseInWorker(open.token);
        }
    }
    /** Loads the thumbnail of a recording, once. */
    notifyLoadThumbnail(recording, broadcast) {
        if (this.thumbnailsRequested.has(recording.recordingId)) {
            return;
        }
        this.thumbnailsRequested.add(recording.recordingId);
        if (!recording.hasThumbnail) {
            this.output.setThumbnail(recording.recordingId, {
                status: 'none',
            });
            return;
        }
        this.output.setThumbnail(recording.recordingId, {
            status: 'loading',
        });
        executeSoon(async () => {
            const indexKey = await this.indexKeyOf(recording, broadcast);
            const response = await fetch(this.objectUrl(recording, RECORDING_THUMBNAIL_OBJECT), { mode: 'cors', credentials: 'omit' });
            if (!response.ok) {
                throw new Error(`thumbnail: ${response.status}`);
            }
            const jpeg = await openWithKey(await importIndexKey(indexKey), new Uint8Array(await response.arrayBuffer()), thumbnailAdditionalData(recording.recordingId));
            this.output.setThumbnail(recording.recordingId, {
                status: 'loaded',
                url: URL.createObjectURL(new Blob([jpeg], { type: 'image/jpeg' })),
            });
        }, () => {
            this.output.setThumbnail(recording.recordingId, {
                status: 'none',
            });
        });
    }
    /**
     * Copies a link that opens the recording. For a private recording the
     * link carries the key of its index, in the fragment.
     */
    notifyCopyLink(recording, broadcast) {
        executeSoon(async () => {
            copyToClipboard(await this.formLink(recording, broadcast));
            showToast('Link copied to clipboard!');
        }, () => {
            showToast('The link could not be made');
        });
    }
    /** Hands the link of the recording to `share` (the system's dialog). */
    notifyShareLink(recording, broadcast, share) {
        executeSoon(async () => {
            share(await this.formLink(recording, broadcast));
        }, () => {
            showToast('The link could not be made');
        });
    }
    async formLink(recording, broadcast) {
        const url = new URL(formBroadcastUrl(broadcast, '/recordings'));
        url.searchParams.set(RECORDING_LINK_PARAM, recording.recordingId);
        if (!recording.isPublic) {
            const indexKey = await this.indexKeyOf(recording, broadcast);
            const fragment = new URLSearchParams(url.hash.replace(/^#/, ''));
            fragment.set(RECORDING_KEY_LINK_PARAM, hexToBase62(Array.from(indexKey, (byte) => byte.toString(16).padStart(2, '0')).join('')));
            url.hash = fragment.toString();
        }
        return url.toString();
    }
    objectUrl(recording, name) {
        const urls = formRecordingUrls();
        if (!urls) {
            throw new Error('Recordings are not available here');
        }
        return recordingObjectUrl(urls.storageUrl, recording.broadcasterUserId, recording.recordingId, name);
    }
    /** The index key, from the link or out of the recording's keys file. */
    async indexKeyOf(recording, broadcast, indexKeyFromLink) {
        const known = this.indexKeys.get(recording.recordingId);
        if (known) {
            return known;
        }
        let indexKey;
        if (indexKeyFromLink) {
            // base62 goes through a number: leading zeros come back here
            indexKey = hexToBinary(base62ToHex(indexKeyFromLink, false).padStart(INDEX_KEY_HEX_LENGTH, '0'));
        }
        else {
            const response = await fetch(this.objectUrl(recording, RECORDING_KEYS_OBJECT), { mode: 'cors', credentials: 'omit' });
            if (!response.ok) {
                throw new Error('The recording was not found');
            }
            const privateKey = findBroadcasterPrivateKey(recording.streamId);
            indexKey = await openKeysFile((await response.json()), {
                broadcasterPrivateKey: privateKey
                    ? hexToBinary(privateKey)
                    : undefined,
                streamKey: hexToBinary(broadcast.aesKey),
            });
        }
        if (!indexKey) {
            throw new Error('This recording is private');
        }
        this.indexKeys.set(recording.recordingId, indexKey);
        return indexKey;
    }
    async openRecording(open, broadcast, indexKeyFromLink) {
        const { recording } = open;
        const urls = formRecordingUrls();
        if (!urls) {
            throw new Error('Recordings are not available here');
        }
        const indexKey = await this.indexKeyOf(recording, broadcast, indexKeyFromLink);
        const response = await fetch(this.objectUrl(recording, RECORDING_INDEX_OBJECT), { mode: 'cors', credentials: 'omit' });
        if (!response.ok) {
            throw new Error('The recording is not ready yet');
        }
        // the signature ties the index, and through its hashes the media,
        // to the broadcaster
        const index = await decodeIndex(new Uint8Array(await response.arrayBuffer()), await importIndexKey(indexKey), {
            verify: verifyBroadcasterSignature,
            broadcasterUserId: recording.broadcasterUserId,
        });
        if (index.recordingId !== recording.recordingId) {
            throw new Error('The recording does not match its index');
        }
        if (this.open !== open) {
            // closed, or another recording was opened, meanwhile
            return;
        }
        open.provision = {
            recordingId: recording.recordingId,
            broadcasterUserId: recording.broadcasterUserId,
            storageUrl: urls.storageUrl,
            streamKey: hexToBinary(broadcast.aesKey),
            index: RecordingIndex.toBinary(index),
            fileName: `${broadcast.name || 'recording'}-${new Date(recording.timestamp)
                .toISOString()
                .slice(0, 16)
                .replace(/[:T]/g, '-')}`.replace(/[^\w.-]+/g, '_'),
        };
        await this.startPlayer(open);
        if (this.open === open && isWebKitBrowser()) {
            this.watchWorker(open);
        }
    }
    /**
     * Gives the recording to the worker under a new token and shows the
     * player of that token. Where workers get lost (WebKit), a worker
     * that goes away during this is registered again, within the limit.
     */
    async startPlayer(open, resume) {
        if (!open.provision) {
            throw new Error('The recording was not opened');
        }
        for (;;) {
            const token = randomToken();
            let reply;
            try {
                reply = await this.provisionWorker({
                    type: 'provision',
                    token,
                    ...open.provision,
                });
            }
            catch (error) {
                if (error instanceof WorkerLostError &&
                    this.open === open &&
                    isWebKitBrowser() &&
                    this.allowRecovery(open)) {
                    continue;
                }
                throw error;
            }
            if (this.open !== open) {
                this.releaseInWorker(token);
                return;
            }
            if (!reply.ok) {
                throw new Error(reply.error);
            }
            if (reply.parts.length === 0) {
                throw new Error('The recording is empty');
            }
            // a worker that is still there has no use for the old player
            this.releaseInWorker(open.token);
            open.token = token;
            open.parts = reply.parts;
            open.resume = resume;
            open.phase = 'playing';
            this.output.setPlayback({
                status: 'ready',
                recordingId: open.recording.recordingId,
                playerUrl: playerDocumentUrl(token),
                partCount: reply.parts.length,
            });
            return;
        }
    }
    /**
     * Registers the worker, which is the existing one where it exists,
     * and hands it the recording.
     */
    async provisionWorker(message) {
        let registration;
        try {
            registration = await registerScopedServiceWorker(RECORDING_SW_URL, RECORDING_SW_SCOPE);
        }
        catch (error) {
            throw new Error(`This browser cannot play recordings (${String(error)})`);
        }
        const worker = registration.active;
        if (!worker) {
            throw new Error('The recording player did not start');
        }
        return new Promise((resolve, reject) => {
            const channel = new MessageChannel();
            const settle = () => {
                clearTimeout(timer);
                worker.removeEventListener('statechange', onStateChange);
            };
            const timer = setTimeout(() => {
                settle();
                reject(new Error('The recording player does not answer'));
            }, PROVISION_TIMEOUT_MS);
            // a worker the browser removes will never answer
            const onStateChange = () => {
                if (worker.state === 'redundant') {
                    settle();
                    reject(new WorkerLostError());
                }
            };
            worker.addEventListener('statechange', onStateChange);
            channel.port1.onmessage = (event) => {
                settle();
                resolve(event.data);
            };
            worker.postMessage(message, [channel.port2]);
        });
    }
    /**
     * Lets the worker forget a player. Only a worker that is there is
     * told: nothing is registered for it, and nothing can fail.
     */
    releaseInWorker(token) {
        if (token === '') {
            return;
        }
        executeSoon(async () => {
            const registration = await getScopedServiceWorkerRegistration(RECORDING_SW_SCOPE);
            registration?.active?.postMessage({ type: 'release', token });
        });
    }
    /** Looks, while the playback is open, whether the worker is still there. */
    watchWorker(open) {
        const check = () => {
            const token = open.token;
            executeSoon(async () => {
                // Only a registration that is gone counts: while a worker
                // is being updated the registration stays.
                if (!(await getScopedServiceWorkerRegistration(RECORDING_SW_SCOPE))) {
                    this.onWorkerLost(open, token, 'registration removed');
                }
            });
        };
        const timer = setInterval(check, WORKER_WATCH_INTERVAL_MS);
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                check();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        open.stopWatching = () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }
    /** Whether the player may be built anew once more. Counts it if so. */
    allowRecovery(open) {
        const now = Date.now();
        open.recoveries = open.recoveries.filter((at) => now - at < RECOVERY_WINDOW_MS);
        if (open.recoveries.length >= RECOVERY_LIMIT) {
            return false;
        }
        open.recoveries.push(now);
        return true;
    }
    /**
     * The player of `token` has no service worker any more. The worker is
     * registered again and a new player takes the place of the old one,
     * where that one was. Told by the watch, by the player document, and
     * by the app when it finds itself loaded in place of the player.
     */
    onWorkerLost(open, token, reason, wantsPlay = false) {
        if (this.open !== open ||
            open.token !== token ||
            open.phase !== 'playing') {
            // closed, an older player, or already being taken care of
            return;
        }
        if (!isWebKitBrowser()) {
            // other browsers keep their workers; nothing changes for them
            console.warn(`[RecordingPlaybackService] service worker lost (${reason})`);
            return;
        }
        if (!this.allowRecovery(open)) {
            this.fail(open, new Error('This browser keeps stopping the recording player'));
            return;
        }
        console.warn(`[RecordingPlaybackService] service worker lost (${reason}), starting the player again`);
        open.phase = 'recovering';
        const position = open.position ?? {
            part: 0,
            currentTimeS: 0,
            paused: true,
            started: false,
        };
        executeSoon(() => this.startPlayer(open, 
        // a press that found no worker is answered by playing
        wantsPlay ? { ...position, paused: false } : position), (error) => {
            this.fail(open, error);
        });
    }
    fail(open, error) {
        if (this.open !== open) {
            return;
        }
        console.error('[RecordingPlaybackService] playback failed', error);
        open.phase = 'failed';
        open.stopWatching?.();
        this.output.setPlayback({
            status: 'error',
            recordingId: open.recording.recordingId,
            message: error instanceof Error
                ? error.message
                : 'The recording cannot be opened',
        });
    }
    /** Messages of the player document (the iframe). */
    onWindowMessage = (event) => {
        const message = event.data;
        const open = this.open;
        if (event.origin !== window.location.origin ||
            !open ||
            typeof message !== 'object' ||
            message === null ||
            open.token === '' ||
            message.token !== open.token ||
            !event.source) {
            return;
        }
        const token = open.token;
        const player = event.source;
        const toPlayer = (reply) => {
            player.postMessage(reply, window.location.origin);
        };
        if (message.type === 'recording-player-ready') {
            toPlayer({
                type: 'recording-player-load',
                token,
                parts: open.parts,
                requirePress: isWebKitBrowser(),
                resume: open.resume,
            });
        }
        else if (message.type === 'recording-player-need-provision') {
            // the worker was restarted and has forgotten the playback
            const provision = open.provision;
            if (!provision) {
                return;
            }
            executeSoon(async () => {
                const reply = await this.provisionWorker({
                    type: 'provision',
                    token,
                    ...provision,
                });
                if (reply.ok && this.open === open && open.token === token) {
                    toPlayer({
                        type: 'recording-player-provisioned',
                        token,
                    });
                }
            });
        }
        else if (message.type === 'recording-player-state') {
            if (message.error) {
                console.warn('[RecordingPlaybackService] player', message.error);
            }
            if (message.controlled === false) {
                this.onWorkerLost(open, token, 'player without its worker', message.wantsPlay === true);
            }
            else if (!message.error && open.phase === 'playing') {
                // only what a player in good health tells is where the
                // viewer is
                open.position = {
                    part: message.part,
                    currentTimeS: message.currentTimeS,
                    paused: message.paused,
                    started: message.started ??
                        (open.position?.started === true || !message.paused),
                };
            }
        }
        else if (message.type === 'recording-player-unserved') {
            this.onWorkerLost(open, token, 'player not served');
        }
    };
}
